import React, { useEffect, useState } from 'react';
import { animated, useTransition } from '@react-spring/web';
import URI from 'urijs';
import { ICONS } from 'Components/utils/icon/icons';
import { LAYER_NAME_UPSELL_ACTIVITIES } from 'Components/utils/layers/layers.constants';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { camelToSnakeCase } from 'Components/utils/string/string.util';
import { useCurrentUri } from 'Src/hooks/use-current-uri/useCurrentUri';
import { useLayer } from 'Src/hooks/use-layer/useLayer';
import { t } from 'Src/i18n';
import { Button } from 'Components/frameworks/button/Button';
import { ActivitiesCategoryCard } from 'Components/widgets/activities-category-card/ActivitiesCategoryCard';
import { UpsellActivitiesCard } from 'Components/widgets/upsell-activities-card/UpsellActivitiesCard';
import { classnames } from 'Utils/classnames/classnames.utils';
type Props = PropsWithPageTracking<{
  categories: Array<ActivitiesCategoryEntity>;
  className?: string;
  colsNumber?: number;
  hasCounters?: boolean;
  hasOnlineBookableCount?: boolean;
  hasOnlineBookableTotal?: boolean;
  layerName?: string;
  layout?: 'default' | 'small' | 'card';
  onClickCard?: (type: string | null | undefined, hasOnlineBooking: boolean) => void;
  onClickMore?: () => void;
}>;
export const ActivitiesCategories = ({
  className,
  categories,
  colsNumber = 4,
  hasCounters = false,
  hasOnlineBookableCount = false,
  hasOnlineBookableTotal = false,
  layerName,
  layout = 'default',
  onClickCard,
  onClickMore,
}: Props) => {
  const currentUri = useCurrentUri();
  const { isTablet, isDesktop } = useResponsive();
  const columnsNumber = isTablet && !isDesktop ? 3 : colsNumber;
  const [shownItems, setShownItems] = useState<number>(columnsNumber);
  const { nextUri } = useLayer(layerName || LAYER_NAME_UPSELL_ACTIVITIES, {
    type: 'CATEGORIES',
  });
  useEffect(() => {
    setShownItems(columnsNumber);
  }, [columnsNumber]);
  const displayExcursionsOnly =
    currentUri.path().startsWith('/l/qr-code') ||
    currentUri.query().indexOf('disableLayout') !== -1;
  const filteredCategories = categories.filter(
    (c) =>
      Boolean(c.categories?.length || c.activities?.length) &&
      (!displayExcursionsOnly || c.id.toLowerCase().indexOf('excursions') !== -1),
  );
  const transitions = useTransition(filteredCategories.slice(0, shownItems), {
    from: {
      marginTop: -75,
      opacity: 0,
    },
    enter: {
      marginTop: 0,
      opacity: 1,
    },
    keys: (item) => item.id,
    config: {
      duration: 150,
    },
    trail: 150,
  });

  const getUrl = (catId) =>
    new URI(nextUri)
      .addQuery({
        catId,
      })
      .toString();

  if (layout === 'small') {
    return filteredCategories.slice(0, 3).map<React.ReactNode>((item) => (
      <div data-testid="activities-category-card" key={item.id}>
        <ActivitiesCategoryCard
          category={item}
          className="h-full min-h-170 w-130 flex-none overflow-hidden border-1 border-solid border-lightGrey focus-within:border-saffron hocus:border-saffron"
          hasCounters={false}
          hasOnlineBookableTotal={hasOnlineBookableTotal}
          isSmall
          key={item.id}
          onClick={() =>
            onClickCard?.(camelToSnakeCase(item.upsellType), item.onlineBookableCount !== 0)
          }
          url={getUrl(item.id)}
        />
      </div>
    ));
  }

  if (layout === 'card') {
    return filteredCategories.map<React.ReactNode>((item) => (
      <UpsellActivitiesCard
        category={item}
        hasOnlineBookableTotal={hasOnlineBookableTotal}
        key={item.id}
        onClick={() =>
          onClickCard?.(camelToSnakeCase(item.upsellType), item.onlineBookableCount !== 0)
        }
        url={getUrl(item.id)}
      />
    ));
  }

  return (
    <section className={className}>
      <section
        className={`grid grid-cols-2 gap-20 md:grid-cols-3 lg:grid-cols-${colsNumber}`}
        data-testid="Activities-all-componant"
      >
        {transitions((styles, item, _key) => {
          const url = getUrl(item.id);
          return (
            <animated.div key={item.id} style={styles} data-testid="activities-category-card">
              <ActivitiesCategoryCard
                category={item}
                className={classnames('h-full transform hover:scale-105', {
                  'min-h-300': hasCounters,
                })}
                hasCounters={hasCounters}
                hasOnlineBookableCount={hasOnlineBookableCount}
                hasOnlineBookableTotal={hasOnlineBookableTotal}
                onClick={() =>
                  onClickCard?.(camelToSnakeCase(item.upsellType), item.onlineBookableCount !== 0)
                }
                url={url}
              />
            </animated.div>
          );
        })}
      </section>
      {shownItems < filteredCategories.length && (
        <div className="mt-40 flex justify-center">
          <Button
            data-testid="Activities-seeMore"
            iconRight={{
              name: ICONS.ARROW_DOWN,
            }}
            onClick={() => {
              setShownItems(shownItems + columnsNumber);
              onClickMore?.();
            }}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}
    </section>
  );
};
